import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Ticket, Users, Gift, Search, ShieldOff, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → "Codici" panel: visualizza referral, famiglia e gift codes con
 * statistiche d'uso e azione di disattivazione/riattivazione one-click.
 */
export default function CodesPanel() {
  return (
    <Tabs defaultValue="referral">
      <TabsList className="bg-parchment-dark rounded-2xl p-1 flex-wrap h-auto" data-testid="codes-subtabs">
        <TabsTrigger value="referral" className="rounded-xl data-[state=active]:bg-white" data-testid="codes-tab-referral">
          <Ticket size={14} className="inline mr-1"/> Referral
        </TabsTrigger>
        <TabsTrigger value="family" className="rounded-xl data-[state=active]:bg-white" data-testid="codes-tab-family">
          <Users size={14} className="inline mr-1"/> Famiglie
        </TabsTrigger>
        <TabsTrigger value="gift" className="rounded-xl data-[state=active]:bg-white" data-testid="codes-tab-gift">
          <Gift size={14} className="inline mr-1"/> Premi
        </TabsTrigger>
      </TabsList>
      <TabsContent value="referral" className="mt-4"><ReferralCodesTab /></TabsContent>
      <TabsContent value="family" className="mt-4"><FamilyCodesTab /></TabsContent>
      <TabsContent value="gift" className="mt-4"><GiftCodesTab /></TabsContent>
    </Tabs>
  );
}

// ---- common ----
function StatusBadge({ active }) {
  return active ? (
    <span className="px-2 py-0.5 rounded-full bg-forest-light text-forest text-[10px] font-bold uppercase tracking-widest">Attivo</span>
  ) : (
    <span className="px-2 py-0.5 rounded-full bg-compass/15 text-compass text-[10px] font-bold uppercase tracking-widest">Disattivo</span>
  );
}

function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"/>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9"
        data-testid={testId}
      />
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <Card className="card-treasure p-8 text-center">
      <Icon className="mx-auto text-ink-500 mb-2" size={32}/>
      <p className="text-ink-700 font-bold">{text}</p>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-10">
      <Loader2 className="animate-spin text-gold" size={36}/>
    </div>
  );
}

// ---- shared toggle hook ----
function useToggleCode(kind, reload) {
  return async (id, currentDisabled) => {
    try {
      await api.post("/admin/codes/disable", { kind, id, disabled: !currentDisabled });
      toast.success(currentDisabled ? "Codice riattivato" : "Codice disattivato");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
}

// ============== REFERRAL ==============
function ReferralCodesTab() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/codes/referral").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const toggle = useToggleCode("referral", load);

  const filtered = useMemo(() => {
    if (!items) return null;
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter((it) =>
      it.code?.toLowerCase().includes(qq) ||
      it.owner_email?.toLowerCase().includes(qq) ||
      it.owner_name?.toLowerCase().includes(qq)
    );
  }, [items, q]);

  if (items === null) return <LoadingState/>;
  return (
    <div className="space-y-3">
      <SearchBar value={q} onChange={setQ} placeholder="Cerca per codice, nome o email…" testId="codes-referral-search"/>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Totali" value={items.length}/>
        <Stat label="Attivi" value={items.filter((i) => !i.disabled).length}/>
        <Stat label="Invitati" value={items.reduce((s, i) => s + i.invitees_count, 0)}/>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={Ticket} text="Nessun codice referral"/>
      ) : (
        <div className="space-y-2" data-testid="codes-referral-list">
          {filtered.map((it) => (
            <Card key={it.owner_user_id} className="card-treasure p-3" data-testid={`codes-referral-${it.code}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                  <span className="font-mono font-extrabold text-ink-900 text-sm tracking-wider">{it.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink-900 truncate">{it.owner_name || "—"}</span>
                    <StatusBadge active={!it.disabled}/>
                  </div>
                  <div className="text-xs text-ink-500 truncate">{it.owner_email}</div>
                  <div className="text-xs text-ink-700 mt-1">
                    <span className="font-bold text-forest">{it.invitees_count}</span> invitati ·{" "}
                    <span className="font-bold text-gold-hover">{it.rewarded_count}</span> premiati
                  </div>
                </div>
                <button
                  onClick={() => toggle(it.owner_user_id, it.disabled)}
                  className={`shrink-0 px-3 py-1.5 rounded-2xl font-bold text-xs flex items-center gap-1 ${
                    it.disabled ? "bg-forest text-white hover:bg-forest-hover" : "bg-compass/10 text-compass hover:bg-compass hover:text-white"
                  }`}
                  data-testid={`codes-referral-toggle-${it.code}`}
                >
                  {it.disabled ? <><ShieldCheck size={12}/> Riattiva</> : <><ShieldOff size={12}/> Disattiva</>}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============== FAMILY ==============
function FamilyCodesTab() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/codes/family").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const toggle = useToggleCode("family", load);

  const filtered = useMemo(() => {
    if (!items) return null;
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter((it) =>
      it.code?.toLowerCase().includes(qq) || it.name?.toLowerCase().includes(qq)
    );
  }, [items, q]);

  if (items === null) return <LoadingState/>;
  return (
    <div className="space-y-3">
      <SearchBar value={q} onChange={setQ} placeholder="Cerca per codice o nome famiglia…" testId="codes-family-search"/>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Famiglie" value={items.length}/>
        <Stat label="Attive" value={items.filter((i) => !i.disabled).length}/>
        <Stat label="Membri totali" value={items.reduce((s, i) => s + i.members_count, 0)}/>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={Users} text="Nessuna famiglia"/>
      ) : (
        <div className="space-y-2" data-testid="codes-family-list">
          {filtered.map((it) => (
            <Card key={it.family_id} className="card-treasure p-3" data-testid={`codes-family-${it.code}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-ocean-light flex items-center justify-center shrink-0">
                  <span className="font-mono font-extrabold text-ocean text-sm tracking-wider">{it.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink-900 truncate">{it.name}</span>
                    <StatusBadge active={!it.disabled}/>
                  </div>
                  <div className="text-xs text-ink-700 mt-1">
                    <span className="font-bold text-forest">{it.members_count}</span> membri ·{" "}
                    <span className="font-bold text-ocean">{it.pending_invites}</span> inviti pendenti
                  </div>
                </div>
                <button
                  onClick={() => toggle(it.family_id, it.disabled)}
                  className={`shrink-0 px-3 py-1.5 rounded-2xl font-bold text-xs flex items-center gap-1 ${
                    it.disabled ? "bg-forest text-white hover:bg-forest-hover" : "bg-compass/10 text-compass hover:bg-compass hover:text-white"
                  }`}
                  data-testid={`codes-family-toggle-${it.code}`}
                >
                  {it.disabled ? <><ShieldCheck size={12}/> Riattiva</> : <><ShieldOff size={12}/> Disattiva</>}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============== GIFT ==============
function GiftCodesTab() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/codes/gift").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const toggle = useToggleCode("gift", load);

  const filtered = useMemo(() => {
    if (!items) return null;
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter((it) =>
      it.code?.toLowerCase().includes(qq) ||
      it.title?.toLowerCase().includes(qq) ||
      it.route_title?.toLowerCase().includes(qq)
    );
  }, [items, q]);

  if (items === null) return <LoadingState/>;
  return (
    <div className="space-y-3">
      <SearchBar value={q} onChange={setQ} placeholder="Cerca per codice, titolo o percorso…" testId="codes-gift-search"/>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Premi" value={items.length}/>
        <Stat label="Attivi" value={items.filter((i) => i.active).length}/>
        <Stat label="Sblocchi totali" value={items.reduce((s, i) => s + i.unlocks_count, 0)}/>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={Gift} text="Nessun codice premio configurato"/>
      ) : (
        <div className="space-y-2" data-testid="codes-gift-list">
          {filtered.map((it) => (
            <Card key={it.reward_id} className="card-treasure p-3" data-testid={`codes-gift-${it.code}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gold-light border border-gold flex items-center justify-center shrink-0">
                  <span className="font-mono font-extrabold text-ink-900 text-[11px] tracking-wider">{it.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink-900 truncate">{it.title}</span>
                    <StatusBadge active={it.active}/>
                  </div>
                  <div className="text-xs text-ink-500 truncate">{it.route_title} · soglia {it.min_percent}%</div>
                  <div className="text-xs text-ink-700 mt-1">
                    <Sparkles size={11} className="inline -mt-0.5 text-gold-hover"/> <span className="font-bold">{it.unlocks_count}</span> sblocchi
                  </div>
                </div>
                <button
                  onClick={() => toggle(it.reward_id, !it.active)}
                  className={`shrink-0 px-3 py-1.5 rounded-2xl font-bold text-xs flex items-center gap-1 ${
                    !it.active ? "bg-forest text-white hover:bg-forest-hover" : "bg-compass/10 text-compass hover:bg-compass hover:text-white"
                  }`}
                  data-testid={`codes-gift-toggle-${it.code}`}
                >
                  {!it.active ? <><ShieldCheck size={12}/> Riattiva</> : <><ShieldOff size={12}/> Disattiva</>}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="card-treasure p-3 text-center">
      <div className="font-display font-extrabold text-2xl text-ink-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">{label}</div>
    </Card>
  );
}
